import { useEffect, useState, useCallback } from "react";
import { Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/oraya-api";
import { useCommand, useRealtime } from "@/context/CommandContext";
import { Panel, Field, inputCls, CCButton, LoadingState } from "@/command/ccUi";

export default function CCUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/cc/users"), api.get("/cc/roles")])
      .then(([u, r]) => { setUsers(u.data); setRoles(r.data); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useRealtime(["users"], load);

  const saveRoles = async () => {
    try { await api.patch(`/cc/users/${editing.user_id}/roles`, { roles: editing.roles }); toast.success("Roles updated"); setEditing(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const createUser = async () => {
    try { await api.post("/cc/users", creating); toast.success("User created"); setCreating(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggle = (list, r) => list.includes(r) ? list.filter((x) => x !== r) : [...list, r];

  if (loading) return <LoadingState />;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="display text-2xl md:text-3xl font-light text-ink">Users & Roles</h1><p className="text-subtle mt-1">Manage who can access the Command Center.</p></div>
        <CCButton onClick={() => setCreating({ email: "", name: "", password: "", roles: ["EMPLOYEE"] })} testid="user-new"><Plus className="w-4 h-4" /> New user</CCButton>
      </div>

      <Panel testid="users-panel">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-subtle border-b border-hairline/[0.08]">
            <th className="px-3 py-3 font-medium">User</th><th className="px-3 py-3 font-medium">Roles</th><th className="px-3 py-3 text-right">Actions</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.user_id} className="border-b border-hairline/[0.05] text-ink" data-testid={`user-row-${u.email}`}>
                <td className="px-3 py-3"><div className="font-medium">{u.name || "—"}</div><div className="text-subtle text-xs">{u.email}</div></td>
                <td className="px-3 py-3"><div className="flex flex-wrap gap-1">{(u.roles || []).length ? u.roles.map((r) => <span key={r} className="text-xs px-2 py-0.5 rounded-full bg-hairline/10 text-subtle">{r}</span>) : <span className="text-subtle text-xs">No access</span>}</div></td>
                <td className="px-3 py-3 text-right"><button onClick={() => setEditing({ user_id: u.user_id, email: u.email, roles: u.roles || [] })} data-testid={`edit-roles-${u.email}`} className="text-xs text-subtle hover:text-ink px-3 py-1.5 rounded-md border border-hairline/15">Edit roles</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setEditing(null)} data-testid="roles-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink">Roles — {editing.email}</h3>
            <div className="grid grid-cols-1 gap-2 mt-4 max-h-80 overflow-y-auto">
              {roles.map((r) => {
                const on = editing.roles.includes(r.key);
                return (
                  <button key={r.key} onClick={() => setEditing({ ...editing, roles: toggle(editing.roles, r.key) })} data-testid={`role-${r.key}`}
                    className={`flex items-center justify-between p-3 rounded-lg text-left transition-colors ${on ? "bg-hairline/[0.08] border border-hairline/20" : "glass-medium"}`}>
                    <div><div className="text-sm text-ink">{r.label}</div><div className="text-xs text-subtle">{r.desc}</div></div>
                    {on && <Check className="w-4 h-4 text-emerald-500" />}
                  </button>
                );
              })}
            </div>
            <div className="flex justify-end gap-3 mt-6"><button onClick={() => setEditing(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button><CCButton onClick={saveRoles} testid="roles-save">Save roles</CCButton></div>
          </div>
        </div>
      )}

      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setCreating(null)} data-testid="newuser-modal">
          <div className="glass-heavy rounded-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="display text-lg font-medium text-ink mb-4">New user</h3>
            <div className="flex flex-col gap-4">
              <Field label="Name"><input className={inputCls} value={creating.name} onChange={(e) => setCreating({ ...creating, name: e.target.value })} data-testid="newuser-name" /></Field>
              <Field label="Email"><input className={inputCls} type="email" value={creating.email} onChange={(e) => setCreating({ ...creating, email: e.target.value })} data-testid="newuser-email" /></Field>
              <Field label="Temporary password"><input className={inputCls} value={creating.password} onChange={(e) => setCreating({ ...creating, password: e.target.value })} data-testid="newuser-password" /></Field>
              <Field label="Roles"><div className="flex flex-wrap gap-2">{roles.map((r) => { const on = creating.roles.includes(r.key); return <button key={r.key} onClick={() => setCreating({ ...creating, roles: toggle(creating.roles, r.key) })} className={`text-xs px-3 py-1.5 rounded-full ${on ? "bg-invert text-invert-ink" : "glass-medium text-subtle"}`} data-testid={`newuser-role-${r.key}`}>{r.label}</button>; })}</div></Field>
            </div>
            <div className="flex justify-end gap-3 mt-6"><button onClick={() => setCreating(null)} className="text-sm text-subtle hover:text-ink px-4 py-2">Cancel</button><CCButton onClick={createUser} testid="newuser-save">Create user</CCButton></div>
          </div>
        </div>
      )}
    </div>
  );
}
